import { Lock } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import type { RoleLevelOption } from "@/hooks/use-role-level-applicability";
import { cn } from "@/lib/utils";

/**
 * The career-level picker (WI-104), rendered inside the role's main edit form
 * rather than as a panel of its own.
 *
 * Purely presentational — the dialog owns the fetch and the selection state, so
 * the levels save can be sequenced with the rest of the role save.
 *
 * Levels are a **set, not a range**: a role may use Associate and Senior while
 * skipping Staff, so nothing here enforces contiguity.
 *
 * A level with people at it is locked with the reason stated inline, because
 * removing it would orphan them. The server enforces the same rule — this just
 * avoids letting the click fail.
 */
export function RoleLevelsField({
  options,
  value,
  onChange,
  loading,
}: {
  options: RoleLevelOption[];
  value: Set<string>;
  onChange: (next: Set<string>) => void;
  loading?: boolean;
}) {
  /** Locked: removing this level would orphan the people currently at it. */
  function isLocked(option: RoleLevelOption) {
    return option.applies && option.peopleCount > 0;
  }

  function toggle(option: RoleLevelOption) {
    if (isLocked(option)) return;
    const next = new Set(value);
    if (next.has(option.id)) next.delete(option.id);
    else next.add(option.id);
    onChange(next);
  }

  if (loading) {
    return <p className="text-sm text-muted-foreground">Loading levels…</p>;
  }

  if (options.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No career levels are configured yet. An admin can add them under Settings → Config → Role
        levels.
      </p>
    );
  }

  return (
    // Taller than a stacked field would need: this sits in the edit dialog's
    // right-hand column, so it has the height of the whole form to fill.
    <div className="max-h-[26rem] space-y-2 overflow-y-auto rounded-md border p-2">
      {options.map((option) => {
        const locked = isLocked(option);
        return (
          <label
            key={option.id}
            htmlFor={`role-level-${option.id}`}
            className={cn(
              "flex items-start gap-3 rounded-md p-2 transition-colors",
              locked ? "cursor-not-allowed bg-muted/40" : "cursor-pointer hover:bg-accent/50",
            )}
          >
            <Checkbox
              id={`role-level-${option.id}`}
              checked={value.has(option.id)}
              disabled={locked}
              onCheckedChange={() => toggle(option)}
              className="mt-0.5"
            />
            <div className="min-w-0 flex-1 space-y-0.5">
              <div className="flex flex-wrap items-center gap-2">
                <span className={cn("text-sm font-medium", locked && "text-muted-foreground")}>
                  {option.name}
                </span>
                {option.peopleCount > 0 && (
                  <span className="text-xs text-muted-foreground">
                    {option.peopleCount} {option.peopleCount === 1 ? "person" : "people"}
                  </span>
                )}
                {option.retired && <Badge variant="secondary">Retired</Badge>}
              </div>
              {locked ? (
                <p className="flex items-center gap-1.5 rounded-sm bg-warning-bg px-2 py-1 text-xs text-warning-foreground">
                  <Lock className="h-3 w-3 shrink-0" />
                  Can't be removed until all {option.peopleCount}{" "}
                  {option.peopleCount === 1 ? "person" : "people"} at this level are moved
                </p>
              ) : (
                option.definition && (
                  <p className="line-clamp-1 text-xs text-muted-foreground">{option.definition}</p>
                )
              )}
            </div>
          </label>
        );
      })}
    </div>
  );
}

/**
 * Read-only display of the levels a role uses, for the role detail header. Shows
 * the count of people at each level where there is one.
 *
 * Renders as a bare list of badges (no wrapper) so it sits alongside the status
 * badge in the header's badge cluster, and renders nothing at all when the role
 * has no levels — a role without a ladder shouldn't grow an empty row.
 */
export function RoleLevelBadges({ options }: { options: RoleLevelOption[] }) {
  const applied = options.filter((o) => o.applies);
  if (applied.length === 0) return null;

  return (
    <>
      {applied.map((level) => (
        <Badge key={level.id} variant={level.retired ? "secondary" : "outline"} className="gap-1.5">
          {level.name}
          {level.retired && <span className="text-muted-foreground">(retired)</span>}
          {level.peopleCount > 0 && (
            <span className="text-muted-foreground">· {level.peopleCount}</span>
          )}
        </Badge>
      ))}
    </>
  );
}
